import type { ReactNode } from 'react';

/**
 * The icon set.
 *
 * Every glyph here was lifted from an inline `<svg>` already shipping somewhere
 * in the app, so adopting `<Icon>` in an existing component is a like-for-like
 * swap rather than a redesign. Two are new (`trash`, `pencil`): the bespoke set
 * had no delete or rename glyph, and the settings screens need both. They are
 * drawn in the same thin-stroke language rather than pulled from an icon
 * library, because mixing a Feather glyph into this set is visible.
 *
 * Geometry is stored at whatever viewBox it was authored in. That is not
 * laziness: re-plotting 33 sets of path coordinates by hand is a good way to
 * introduce a silently wrong curve, and it would make every future migration a
 * visual diff instead of a text one. What *is* normalised is stroke weight, in
 * `Icon.tsx` — see the note there.
 *
 * `weight` overrides that normalisation for the few glyphs that need a heavier
 * line to stay legible: a checkmark is two strokes with nothing around them, so
 * at the shared weight it reads as fainter than the icons beside it.
 */

export interface IconDef {
  /** Authored viewBox. Only square viewBoxes are supported. */
  viewBox: string;
  body: ReactNode;
  /** Multiplier on the shared stroke weight. Defaults to 1. */
  weight?: number;
  /** Solid glyph: painted with fill, no stroke. */
  filled?: boolean;
}

export const ICONS = {
  /* ---- Direction ------------------------------------------------------- */
  'chevron-down': {
    viewBox: '0 0 16 16',
    body: <polyline points="4 6 8 10 12 6" />,
  },
  'chevron-up': {
    viewBox: '0 0 16 16',
    body: <polyline points="4 10 8 6 12 10" />,
  },
  'chevron-left': {
    viewBox: '0 0 16 16',
    body: <polyline points="10 4 6 8 10 12" />,
  },
  'chevron-right': {
    viewBox: '0 0 16 16',
    body: <polyline points="6 4 10 8 6 12" />,
  },
  'arrow-right': {
    viewBox: '0 0 16 16',
    body: (
      <>
        <line x1="3" y1="8" x2="12" y2="8" />
        <polyline points="8 4 12 8 8 12" />
      </>
    ),
  },
  'arrow-up': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </>
    ),
  },
  /** Leaves the app. Pair it with target="_blank" and nothing else. */
  'arrow-external': {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M10 2h4v4" />
        <line x1="14" y1="2" x2="7" y2="9" />
      </>
    ),
  },
  'external-link': {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M7 3H3a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V9" />
        <path d="M10 2h4v4" />
        <line x1="14" y1="2" x2="7" y2="9" />
      </>
    ),
  },

  /* ---- Actions --------------------------------------------------------- */
  close: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <line x1="3" y1="3" x2="13" y2="13" />
        <line x1="13" y1="3" x2="3" y2="13" />
      </>
    ),
  },
  plus: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
  },
  search: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <circle cx="6.5" cy="6.5" r="4.5" />
        <line x1="10.5" y1="10.5" x2="14" y2="14" />
      </>
    ),
  },
  check: {
    viewBox: '0 0 16 16',
    weight: 1.25,
    body: <polyline points="2 8 6 12 14 4" />,
  },
  'check-circle': {
    viewBox: '0 0 16 16',
    weight: 1.15,
    body: (
      <>
        <circle cx="8" cy="8" r="6.5" />
        <polyline points="5 8 7 10 11 6" />
      </>
    ),
  },
  copy: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <rect x="5" y="5" width="9" height="10" rx="1.5" />
        <path d="M3 11V2.5A1.5 1.5 0 014.5 1H11" />
      </>
    ),
  },
  paperclip: {
    viewBox: '0 0 24 24',
    body: (
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    ),
  },
  upload: {
    viewBox: '0 0 24 24',
    body: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />,
  },
  stop: {
    viewBox: '0 0 24 24',
    filled: true,
    body: <rect x="6" y="6" width="12" height="12" rx="2" />,
  },
  'more-horizontal': {
    viewBox: '0 0 16 16',
    filled: true,
    body: (
      <>
        <circle cx="2" cy="8" r="1.5" />
        <circle cx="8" cy="8" r="1.5" />
        <circle cx="14" cy="8" r="1.5" />
      </>
    ),
  },
  /** New to the set. No existing component had a delete glyph. */
  trash: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M2.5 4h11" />
        <path d="M6 4V2.8A.8.8 0 016.8 2h2.4a.8.8 0 01.8.8V4" />
        <path d="M12.3 4l-.5 8.7a1.3 1.3 0 01-1.3 1.3H5.5a1.3 1.3 0 01-1.3-1.3L3.7 4" />
        <line x1="6.6" y1="6.8" x2="6.8" y2="11.4" />
        <line x1="9.4" y1="6.8" x2="9.2" y2="11.4" />
      </>
    ),
  },
  /** New to the set. Rename lives in a menu today with no icon beside it. */
  pencil: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M11.1 2.4a1.6 1.6 0 012.3 2.3L5.6 12.5 2.5 13.5l1-3.1z" />
        <line x1="10.2" y1="3.4" x2="12.4" y2="5.6" />
      </>
    ),
  },
  /** Invite someone into the organisation. Lifted from the invite button the
      org-invites work added to the sidebar. */
  'user-plus': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="16" y1="11" x2="22" y2="11" />
      </>
    ),
  },

  /* ---- Feedback -------------------------------------------------------- */
  'thumbs-up': {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M5 7V13H11.5L13 10V7H10L9.5 3C9.5 2.4 9 2 8.5 2C8 2 7.5 2.4 7.5 3V7H5Z" />
        <path d="M5 7H3V13H5" />
      </>
    ),
  },
  'thumbs-down': {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M11 9V3H4.5L3 6V9H6L6.5 13C6.5 13.6 7 14 7.5 14C8 14 8.5 13.6 8.5 13V9H11Z" />
        <path d="M11 9H13V3H11" />
      </>
    ),
  },
  /** The "help us improve" glyph: a speaker, not a megaphone. */
  feedback: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1z" />
        <path d="M15 8.5a4 4 0 010 7" />
        <path d="M18 6a7.5 7.5 0 010 12" />
      </>
    ),
  },

  /* ---- Documents ------------------------------------------------------- */
  file: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
        <polyline points="10 2 10 5 13 5" />
      </>
    ),
  },
  'file-lines': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
      </>
    ),
  },

  /* ---- Settings and session -------------------------------------------- */
  sun: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="4" />
        <line x1="12" y1="2" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="22" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="2" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </>
    ),
  },
  moon: {
    viewBox: '0 0 24 24',
    body: <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />,
  },
  monitor: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    ),
  },
  gear: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </>
    ),
  },
  crosshair: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
        <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
        <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
        <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
        <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
      </>
    ),
  },
  'sign-out': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </>
    ),
  },

  /* ---- Subject matter --------------------------------------------------
     The domain glyphs from the welcome screen. Water, model, terrain, result. */
  droplet: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <circle cx="8" cy="8" r="6.2" />
        <polygon points="8 4.6 9.8 8 8 11.4 6.2 8" />
      </>
    ),
  },
  grid: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <rect x="2" y="2" width="5" height="5" rx="1" />
        <rect x="9" y="2" width="5" height="5" rx="1" />
        <rect x="2" y="9" width="5" height="5" rx="1" />
        <rect x="9" y="9" width="5" height="5" rx="1" />
      </>
    ),
  },
  layers: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <polygon points="2 4 6 2 10 4 14 2 14 12 10 14 6 12 2 14" />
        <line x1="6" y1="2" x2="6" y2="12" />
        <line x1="10" y1="4" x2="10" y2="14" />
      </>
    ),
  },
  /* The welcome screen's "target" glyph is not here: it and `crosshair` above
     are the same drawing at two viewBox sizes, and once stroke weight is
     normalised they render indistinguishably. Two names for one glyph is a
     choice at every call site with no right answer, so `crosshair` covers both. */
  help: {
    viewBox: '0 0 16 16',
    body: (
      <>
        <circle cx="8" cy="8" r="6.2" />
        <path d="M6.1 6.3a1.9 1.9 0 1 1 3.1 1.5c-.6.5-1.1.8-1.1 1.7" />
        <circle cx="8" cy="11.6" r="0.4" fill="currentColor" stroke="none" />
      </>
    ),
  },
  /* New, for What's new: a four-point star with curved sides, the usual mark for
     "something changed" that does not read as a notification bell. */
  sparkle: {
    viewBox: '0 0 24 24',
    body: <path d="M12 3c.7 5 3.3 7.6 9 9-5.7 1.4-8.3 4-9 9-.7-5-3.3-7.6-9-9 5.7-1.4 8.3-4 9-9z" />,
  },
} as const satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;

/** Sorted, for the gallery and for anything that needs to enumerate the set. */
export const ICON_NAMES = Object.keys(ICONS).sort() as IconName[];
